import { useCustomToast, useToast } from '@hover/blueprint';
import { useCompositorControls } from '@hoverinc/rendering-compositor-react';
import { useActor } from '@xstate/react';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  EmptyDesignConfig,
  FeatureCategories,
} from 'src/modules/designStudio/constants';
import {
  DesignStudioServiceContext,
  productCatalogContext,
} from 'src/modules/designStudio/providers';
import type { UpdateDesignFunction } from 'src/modules/designStudio/providers/HistoryProvider';
import type { LegacyDesignConfig } from 'src/modules/designStudio/types';
import { StandalonePassiveToast } from 'src/shared/components/Toast/components/PassiveToast';
import type { HiDefRenderingFile } from 'src/shared/graphql/generated';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';

export interface InteractiveRegionSettings {
  color: string | null;
  textureId: number | null;
}

const NON_TEXTURED_CATEGORIES = [FeatureCategories.TRIM];
const PLACEHOLDER_TEXTURE =
  'data:image/x-ms-bmp;base64,Qk05AAAAAAAAADYAAAAoAAAAAQAAAAEAAAABABgAAAAAAAMAAAAAAAAAAAAAAAAAAAAAAAAA////AA==';

const DEFAULT_TEXTURE_WIDTH = 48;
const DEFAULT_TEXTURE_HEIGHT = 48;

export const emptyIRRegionSettings: InteractiveRegionSettings = {
  color: null,
  textureId: null,
};

interface InteractiveRenderingStateContext {
  clickedRegion: { regionId: string };
  designConfig: LegacyDesignConfig;
  loadError?: Error;
  regionSettings: {
    [key: string]: InteractiveRegionSettings;
  };
  renderingFile?: HiDefRenderingFile;
  renderingFileNotFound: boolean;
  renderingName: string;
  setShowHelpOverlay: (value: boolean) => void;
  showHelpOverlay: boolean;
  updateDesign: UpdateDesignFunction;
}

const context = createContext<InteractiveRenderingStateContext>({
  clickedRegion: { regionId: '' },
  designConfig: EmptyDesignConfig,
  regionSettings: {},
  renderingFileNotFound: false,
  renderingName: '',
  setShowHelpOverlay: () => {},
  showHelpOverlay: false,
  updateDesign: () => null,
});

const TEXTURE_APPLICATION_TOAST_ID = 'texture-application-toast';
const TEXTURE_APPLICATION_TOAST_DELAY = 100;

const InteractiveRenderingStateProvider = ({
  children,
  clickedRegion,
  loadError,
  renderingFile,
  renderingFileNotFound = false,
  renderingName,
}: {
  children: React.ReactNode;
  clickedRegion: { regionId: string };
  loadError?: Error;
  renderingFile: HiDefRenderingFile | undefined;
  renderingFileNotFound?: boolean;
  renderingName: string;
}) => {
  const t = useTranslate(TranslationNamespace.DESIGN_STUDIO);
  const { selectedRegion, setRegionTexture } = useCompositorControls();
  const [regionSettings, setRegionSettings] = useState<{
    [key: string]: InteractiveRegionSettings;
  }>({});
  const { appService } = useContext(DesignStudioServiceContext);
  const [appState] = useActor(appService);
  const [showHelpOverlay, setShowHelpOverlay] = useState(false);

  const [designConfig, setDesignConfig] =
    useState<LegacyDesignConfig>(EmptyDesignConfig);

  const { textures } = useContext(productCatalogContext);

  const errorToast = useToast({ position: 'top', status: 'error' });
  const applyToast = useCustomToast({
    position: 'top',
    render: () => (
      <StandalonePassiveToast
        spinner
        title={t('interactiveRenderings.applying')}
      />
    ),
  });

  useEffect(() => {
    if (appState.value === 'idle') {
      // Wait 850 milliseconds after the app becomes idle and then display the help overlay
      void (async () => {
        await new Promise<void>(resolve => {
          setTimeout(() => resolve(), 850);
        });

        setShowHelpOverlay(true);
      })();
    }
  }, [appState]);

  useEffect(() => {
    // If region is selected while the help overlay is being shown, immediately hide the overlay
    if (showHelpOverlay && selectedRegion) {
      setShowHelpOverlay(false);
    }
    // showHelpOverlay is intentionally excluded from the deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRegion]);

  const updateDesign: UpdateDesignFunction = useCallback(
    async ({ update, category }) => {
      setDesignConfig(previousDesignConfig => ({
        ...previousDesignConfig,
        [category]: update,
      }));

      if (selectedRegion) {
        const doesNotRequireTexture =
          NON_TEXTURED_CATEGORIES.includes(category);

        const previousRegionSettings = regionSettings;
        const color =
          update.color || previousRegionSettings?.[selectedRegion]?.color;

        const textureId =
          update.textureId ||
          previousRegionSettings?.[selectedRegion]?.textureId;
        const selectedTexture = textures?.find(({ id }) => id === textureId);
        const textureUrl = selectedTexture?.texture?.diffuse_map?.url;
        const textureWidth = selectedTexture?.width ?? DEFAULT_TEXTURE_WIDTH;
        const textureHeight = selectedTexture?.height ?? DEFAULT_TEXTURE_HEIGHT;

        if (color && (textureUrl || doesNotRequireTexture)) {
          let timeout: ReturnType<typeof setTimeout> | undefined;

          if (!applyToast.isActive(TEXTURE_APPLICATION_TOAST_ID)) {
            timeout = setTimeout(
              () => applyToast({ id: TEXTURE_APPLICATION_TOAST_ID }),
              TEXTURE_APPLICATION_TOAST_DELAY,
            );
          }

          try {
            await setRegionTexture(textureUrl ?? PLACEHOLDER_TEXTURE, {
              tint: color,
              width: textureWidth,
              height: textureHeight,
            });
          } catch (_) {
            errorToast({
              description: t('interactiveRenderings.textureApplicationFailed'),
            });
          } finally {
            clearTimeout(timeout);
            applyToast.close(TEXTURE_APPLICATION_TOAST_ID);
          }
        }

        const newRegionSettings = {
          ...previousRegionSettings,
          [selectedRegion]: {
            color,
            textureId,
          },
        };

        setRegionSettings(newRegionSettings);
      }
    },
    [
      selectedRegion,
      regionSettings,
      textures,
      applyToast,
      setRegionTexture,
      errorToast,
      t,
    ],
  );

  const value = useMemo(
    () => ({
      clickedRegion,
      designConfig,
      loadError,
      updateDesign,
      regionSettings,
      renderingFile,
      renderingFileNotFound,
      renderingName,
      showHelpOverlay,
      setShowHelpOverlay,
    }),
    [
      clickedRegion,
      designConfig,
      loadError,
      updateDesign,
      regionSettings,
      renderingFile,
      renderingFileNotFound,
      renderingName,
      showHelpOverlay,
      setShowHelpOverlay,
    ],
  );

  return <context.Provider value={value}>{children}</context.Provider>;
};

export {
  InteractiveRenderingStateProvider,
  context as InteractiveRenderingStateContext,
};
